﻿// tslint:disable:all

import axios, { AxiosRequestConfig, AxiosError, AxiosResponse } from "axios";
import { AppConfig } from "@infrastructure/config/config";
import * as _ from "lodash";
import DateTimeHelper from "@app/shared/helpers/datetimeHelper";
import * as Qs from "qs";
import { WellKnownRouter } from "@infrastructure/config/router";

export class HttpService {

    private static _instance: HttpService;

    private requestId: number = 0;

    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    public request<TSuccessData, TFailData>(httpConfig: CustomAxiosRequestConfig): Promise<OperationResult<TSuccessData, TFailData>> {
        if (httpConfig.method.toLowerCase() === "get") {
            return this.handleGet<TSuccessData, TFailData>(httpConfig);
        } else {
            return this.handlePost<TSuccessData, TFailData>(httpConfig);
        }
    }

    public GetUrl(route: string, controller: string, action: string): string {
        let controllerName = controller.replace("Controller", "");

        const param: string[] = route.split("?");
        if (param[1]) {
            return `${controllerName}/${action}?${param[1]}`;
        }

        return `${controllerName}/${action}`;
    }

    private handlePost<TSuccessData, TFailData>(httpConfig: CustomAxiosRequestConfig): Promise<OperationResult<TSuccessData, TFailData>> {
        return axios.get(this.getTenantUrl("/Security/GetXsrfToken"), {
            headers: {
                "X-Requested-With": "XMLHttpRequest"
            }
        })
            .then(response => {
                if (response.data.isSuccessful) {
                    let token = response.data.successData;
                    httpConfig.headers = { 
                        "X-XSRF-TOKEN": token,
                        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                        "X-Requested-With": "XMLHttpRequest"
                    };
                    httpConfig.url = this.getTenantUrl(httpConfig.url);
                    httpConfig.requestId = ++this.requestId;
                    httpConfig.data = Qs.stringify(httpConfig.data);
                    let postResponse = axios.request<OperationResult<TSuccessData, TFailData>>(httpConfig);
                    return new Promise<OperationResult<TSuccessData, TFailData>>((resolve, reject) => {
                        postResponse.then((success: AxiosResponse<OperationResult<TSuccessData, TFailData>>) => {
                            this.handleAjaxResponse<TSuccessData, TFailData>(success, resolve, httpConfig);
                        }).catch((error: AxiosError) => {
                            this.handleError(error, httpConfig);
                        });
                    });
                }
            });
    }

    private handleGet<TSuccessData, TFailData>(httpConfig: CustomAxiosRequestConfig): Promise<OperationResult<TSuccessData, TFailData>> {
        httpConfig.url = this.getTenantUrl(httpConfig.url);
        httpConfig.params = httpConfig.data;
        httpConfig.requestId = ++this.requestId;
        httpConfig.paramsSerializer = (params) => {
            return Qs.stringify(
                params,
                {
                    arrayFormat: "repeat",
                    serializeDate: (date) => {
                        return DateTimeHelper.toServerDateFormatString(date);
                    }
                });
        };
        let response = axios.request<OperationResult<TSuccessData, TFailData>>(httpConfig);

        return new Promise<OperationResult<TSuccessData, TFailData>>((resolve, reject) => {
            response.then((success) => {
                this.handleAjaxResponse<TSuccessData, TFailData>(success, resolve, httpConfig);
            }).catch((error: AxiosError) => {
                this.handleError(error, httpConfig);
            });
        });
    }

    private handleError(error: AxiosError, httpConfig: CustomAxiosRequestConfig): void {
        if (httpConfig.disableErrorHandling) {
            return;
        }

        if (error.response) {
            // the request was made and the server responded with a status code
            // that falls out of the range of 2xx
            this.onError(error.response.status);
        } else {
            // log to datadog
        }
    }

    private handleAjaxResponse<TSuccessData, TFailData>(response: AxiosResponse<OperationResult<TSuccessData, TFailData>>,
        resolve: (value?: OperationResult<TSuccessData, TFailData> | PromiseLike<OperationResult<TSuccessData, TFailData>>) => void, httpConfig: CustomAxiosRequestConfig) {
        if (response.status == 200) {
            if (typeof response.data == "object") {
                let data = response.data;
                switch (data.status) {
                    case OperationResultStatus.NotFound:
                        this.onError(404);
                        break;
                    case OperationResultStatus.Unauthorized:
                        this.onError(401);
                        break;
                    default:
                        data.requestId = (response.config as CustomAxiosRequestConfig).requestId;
                        resolve(data);
                }
            } else {
                throw new Error("Unexpected response: " + response.config.url + JSON.stringify(response.data));
            }
        } else {
            this.onError(response.status);
        }
    }

    private getTenantUrl(url: string): string {
        let tenant = AppConfig.Instance.getTenant().toLowerCase();

        if (location.pathname.toLowerCase().indexOf(`/${tenant}`) >= 0) {
            return `/${tenant}/${url}`;
        }

        return `/${_.trim(url,"/")}`;
    }

    private isSigningOut: boolean = false;
    private onError(errorStatus) {
        const router = AppConfig.Instance.getVueRouter();
        if (this.isSigningOut) {
            return;
        }
        if (errorStatus === 403) {
            this.isSigningOut = true;
            location.reload();
        } else {
            router.push({ name: WellKnownRouter.Error, params: { errorCode: errorStatus } });
        }
    }
}

enum OperationResultStatus {
    Successful = 0,
    GeneralError = 1,
    NotFound = 2,
    InvalidArguments = 3,
    Unauthorized = 4,
    FailedValidation = 5
}

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    requestId?: number;
    disableErrorHandling?: boolean;
}
